export class Input {
  private readonly keysDown = new Set<string>();
  private readonly keysPressed = new Set<string>();
  private readonly keysReleased = new Set<string>();

  private mouseDX = 0;
  private mouseDY = 0;
  /** True while Pointer Lock is held on the canvas. */
  private locked = false;
  /**
   * True while the player intends to be in-game. Survives pointer-lock
   * denial / instant revoke (Cursor Simple Browser). Cleared only by exitLock().
   */
  private playCapture = false;
  private lastClientX: number | null = null;
  private lastClientY: number | null = null;
  private readonly buttonsDown = new Set<number>();
  private readonly buttonsPressed = new Set<number>();
  private readonly buttonsReleased = new Set<number>();
  private wheel = 0;

  private readonly canvas: HTMLElement;
  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;
  private readonly onMouseMove: (e: MouseEvent) => void;
  private readonly onMouseDown: (e: MouseEvent) => void;
  private readonly onMouseUp: (e: MouseEvent) => void;
  private readonly onWheel: (e: WheelEvent) => void;
  private readonly onPointerLockChange: () => void;
  private readonly onContextMenu: (e: Event) => void;
  private readonly onBeforeUnload: (e: BeforeUnloadEvent) => void;

  constructor(canvas: HTMLElement) {
    this.canvas = canvas;

    this.onKeyDown = (e) => {
      // Escape exits play capture only (fullscreen is a separate toggle)
      if (e.code === 'Escape') {
        if (this.isLocked) this.exitLock();
        return;
      }
      if (e.code === 'F3' || e.code === 'F4') e.preventDefault();
      // Cancel every default the page is allowed to cancel while playing.
      // Ctrl+W / Ctrl+Shift+W (sprint+crouch+forward) close the tab unless
      // fullscreen + Keyboard Lock are active — see requestLock().
      if (this.isLocked) {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) e.stopImmediatePropagation();
      }
      if (e.repeat) return;
      this.keysDown.add(e.code);
      this.keysPressed.add(e.code);
    };

    this.onKeyUp = (e) => {
      this.keysDown.delete(e.code);
      this.keysReleased.add(e.code);
    };

    this.onMouseMove = (e) => {
      if (!this.playCapture) return;
      if (this.locked) {
        this.mouseDX += e.movementX;
        this.mouseDY += e.movementY;
        return;
      }
      // No pointer lock — client deltas (movementX is often 0 in embedded browsers)
      if (this.lastClientX !== null && this.lastClientY !== null) {
        this.mouseDX += e.clientX - this.lastClientX;
        this.mouseDY += e.clientY - this.lastClientY;
      }
      this.lastClientX = e.clientX;
      this.lastClientY = e.clientY;
    };

    this.onMouseDown = (e) => {
      if (!this.playCapture) return;
      this.buttonsDown.add(e.button);
      this.buttonsPressed.add(e.button);
      if (!this.locked) {
        this.lastClientX = e.clientX;
        this.lastClientY = e.clientY;
      }
    };

    this.onMouseUp = (e) => {
      this.buttonsDown.delete(e.button);
      this.buttonsReleased.add(e.button);
    };

    this.onWheel = (e) => {
      if (!this.isLocked) return;
      e.preventDefault();
      this.wheel += Math.sign(e.deltaY);
    };

    this.onPointerLockChange = () => {
      this.locked = document.pointerLockElement === this.canvas;
      if (this.locked) {
        this.lastClientX = null;
        this.lastClientY = null;
        return;
      }
      this.buttonsDown.clear();
      // Still playing without lock — keep client-delta look and keyboard lock
      if (this.playCapture) {
        this.lastClientX = null;
        this.lastClientY = null;
        return;
      }
      this.unlockKeyboard();
      if (document.fullscreenElement) {
        void document.exitFullscreen().catch(() => undefined);
      }
    };

    this.onContextMenu = (e) => e.preventDefault();

    this.onBeforeUnload = (e) => {
      if (!this.playCapture) return;
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('keydown', this.onKeyDown, true);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('beforeunload', this.onBeforeUnload);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mousedown', this.onMouseDown);
    document.addEventListener('mouseup', this.onMouseUp);
    document.addEventListener('wheel', this.onWheel, { passive: false });
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
    this.canvas.addEventListener('contextmenu', this.onContextMenu);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown, true);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('wheel', this.onWheel);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
  }

  /**
   * Enter play capture. Fullscreen + Keyboard Lock let Chrome actually honor
   * preventDefault on Ctrl+W/T/N. Pointer lock is best-effort; if denied,
   * client-delta look still works.
   */
  async requestLock(): Promise<void> {
    this.playCapture = true;
    this.lastClientX = null;
    this.lastClientY = null;

    if (!document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen();
      } catch {
        // Denied / embedded — Keyboard Lock will likely fail; beforeunload is the fallback
      }
    }

    await this.lockKeyboard();

    if (document.pointerLockElement !== this.canvas) {
      try {
        const maybePromise = this.canvas.requestPointerLock() as void | Promise<void>;
        if (maybePromise && typeof (maybePromise as Promise<void>).then === 'function') {
          await maybePromise;
        }
      } catch {
        // Denied / unsupported — playCapture keeps client-delta look alive
      }
    }

    this.locked = document.pointerLockElement === this.canvas;
  }

  exitLock(): void {
    this.playCapture = false;
    this.lastClientX = null;
    this.lastClientY = null;
    this.buttonsDown.clear();
    this.unlockKeyboard();
    if (document.pointerLockElement === this.canvas) {
      document.exitPointerLock();
    }
    // Fullscreen is independent — do not exit it here
  }

  /** Re-apply Keyboard Lock after fullscreen actually lands. */
  relockKeyboard(): Promise<void> {
    return this.lockKeyboard();
  }

  private async lockKeyboard(): Promise<void> {
    const keyboard = navKeyboard();
    if (!keyboard?.lock) return;
    try {
      // Lock everything we can so Ctrl+Shift+W (run + duck + forward) cannot
      // close the tab. Empty lock() = all keys (Chrome). Fallback lists W/T/N
      // plus the modifiers used for sprint/crouch.
      await keyboard.lock();
    } catch {
      try {
        await keyboard.lock([
          'KeyW',
          'KeyT',
          'KeyN',
          'ControlLeft',
          'ControlRight',
          'ShiftLeft',
          'ShiftRight',
        ]);
      } catch {
        // Unsupported or not fullscreen
      }
    }
  }

  private unlockKeyboard(): void {
    try {
      navKeyboard()?.unlock?.();
    } catch {
      // ignore
    }
  }

  /** Menu / HUD toggle — not tied to play / pointer lock. */
  async toggleFullscreen(): Promise<boolean> {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return false;
      }
      await document.documentElement.requestFullscreen();
      return true;
    } catch {
      return !!document.fullscreenElement;
    }
  }

  get isFullscreen(): boolean {
    return !!document.fullscreenElement;
  }

  /** True while play input is active (pointer lock OR embedded fallback). */
  get isLocked(): boolean {
    return this.playCapture;
  }

  get usingFallbackLook(): boolean {
    return this.playCapture && !this.locked;
  }

  isDown(code: string): boolean {
    return this.keysDown.has(code);
  }

  wasPressed(code: string): boolean {
    return this.keysPressed.has(code);
  }

  wasReleased(code: string): boolean {
    return this.keysReleased.has(code);
  }

  /** Consume mouse delta for this frame (degrees of movement in pixels). */
  consumeMouseDelta(): { x: number; y: number } {
    const x = this.mouseDX;
    const y = this.mouseDY;
    this.mouseDX = 0;
    this.mouseDY = 0;
    return { x, y };
  }

  /** Call at end of frame to clear pressed/released edges. */
  endFrame(): void {
    this.keysPressed.clear();
    this.keysReleased.clear();
    this.buttonsPressed.clear();
    this.buttonsReleased.clear();
    this.wheel = 0;
  }

  isMouseDown(button: number): boolean {
    return this.buttonsDown.has(button);
  }

  wasMousePressed(button: number): boolean {
    return this.buttonsPressed.has(button);
  }

  consumeWheel(): number {
    const w = this.wheel;
    this.wheel = 0;
    return w;
  }

  get fireHeld(): boolean {
    return this.isMouseDown(0);
  }

  get firePressed(): boolean {
    return this.wasMousePressed(0);
  }

  get adsHeld(): boolean {
    return this.isMouseDown(2);
  }

  get reloadPressed(): boolean {
    return this.wasPressed('KeyR');
  }

  get interactPressed(): boolean {
    return this.wasPressed('KeyF');
  }

  get moveAxis(): { x: number; y: number } {
    let x = 0;
    let y = 0;
    if (this.isDown('KeyD') || this.isDown('ArrowRight')) x += 1;
    if (this.isDown('KeyA') || this.isDown('ArrowLeft')) x -= 1;
    if (this.isDown('KeyW') || this.isDown('ArrowUp')) y += 1;
    if (this.isDown('KeyS') || this.isDown('ArrowDown')) y -= 1;
    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    return { x, y };
  }

  get sprinting(): boolean {
    return this.isDown('ShiftLeft') || this.isDown('ShiftRight');
  }

  get crouching(): boolean {
    return this.isDown('ControlLeft') || this.isDown('ControlRight') || this.isDown('KeyC');
  }

  get crouchPressed(): boolean {
    return (
      this.wasPressed('ControlLeft') ||
      this.wasPressed('ControlRight') ||
      this.wasPressed('KeyC')
    );
  }

  get jumpPressed(): boolean {
    return this.wasPressed('Space');
  }

  get musicTogglePressed(): boolean {
    return this.wasPressed('KeyM');
  }

  get inspectPressed(): boolean {
    return this.wasPressed('F3');
  }

  get copyDebugPressed(): boolean {
    return this.wasPressed('F4');
  }

  /** -1 = lean left (Q), +1 = lean right (E), 0 = none */
  get leanAxis(): number {
    let v = 0;
    if (this.isDown('KeyQ')) v -= 1;
    if (this.isDown('KeyE')) v += 1;
    return v;
  }
}

type KeyboardAPI = {
  lock: (codes?: string[]) => Promise<void>;
  unlock: () => void;
};

function navKeyboard(): KeyboardAPI | undefined {
  return (navigator as Navigator & { keyboard?: KeyboardAPI }).keyboard;
}
